import { Spinner, Text } from "@fluentui/react-components";
import { addStartedInterview } from "api";
import {
  acceptConsent,
  areUrlParamsValid,
  EXTERNAL_SESSION,
  getConsent,
  getDiagnosticByIdExternal,
  getNextObjectiveExternal,
  getPrivacyNoticeStatus,
  getSessionInfo,
  recordPrivacyNotice,
  setConsent,
} from "apps/diagnostics/api";
import { buildAccentPalette, IAccentPalette } from "utils/accentPalette";
import { ExternalDiagnostics } from "apps/diagnostics/externalDiagnostics/externalDiagnostics";
import { ExternalDiagnosticsClosing } from "apps/diagnostics/externalDiagnostics/externalDiagnosticsClosing/ExternalDiagnosticsClosing";
import {
  ExternalDiagnosticsConsent,
  TERMS_AND_CONDITIONS_URL,
} from "apps/diagnostics/externalDiagnostics/externalDiagnosticsConsent/externalDiagnosticsConsent";
import { ExternalDiagnosticsInvalidUrl } from "apps/diagnostics/externalDiagnostics/externalDiagnosticsInvalidUrl/ExternalDiagnosticsInvalidUrl";
import { IExternalDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import { INextObjectiveResponse } from "apps/diagnostics/models/INextQuestionResponse";
import { DataPrivacyNoticePage } from "apps/diagnostics/pages/dataPrivacyNotice/DataPrivacyNoticePage";
import { Constants, ConstantValues, PAGE_TITLES } from "common/constants";
import { ExternalLayout } from "layouts/externalLayout/externalLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { showToast } from "../../../../redux/toastActions";
import { useStyles } from "./externalDiagnosticsPage.styles";

const CONSENT_ACCEPTED = "accepted";
const CONSENT_DECLINED = "declined";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const SPINNER_BRANDING_KEY = "nhDiagnosticSpinnerBranding";

export const ExternalDiagnosticsPage = () => {
  const { id: diagnosticId } = useParams<{ id: string }>();
  const hasFetchedInitialData = useRef<boolean>(false);
  const dispatch = useDispatch();
  const styles = useStyles();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const distributionIdFromUrl = queryParams.get("distributionId");

  const [diagnostic, setDiagnostic] = useState<IExternalDiagnostic | null>(
    null,
  );
  const [foundDiagnosticId, setFoundDiagnosticId] = useState<string>();
  const [distributionId, setDistributionId] = useState<string>();
  const [isSpeechEnabled, setIsSpeechEnabled] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showConsent, setShowConsent] = useState<boolean>(
    getConsent() !== CONSENT_ACCEPTED,
  );
  const [privacyLoading, setPrivacyLoading] = useState<boolean>(true);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState<boolean>(false);
  const [showClosingPage, setShowClosingPage] = useState<boolean>(false);
  const [initialNextObjective, setInitialNextObjective] =
    useState<INextObjectiveResponse | null>(null);

  const [isInvalidUrl, setIsInvalidUrl] = useState<boolean>(false);

  const accentPalette: IAccentPalette | undefined = useMemo(
    () =>
      diagnostic?.customBrandingEnabled
        ? buildAccentPalette(diagnostic.accentColor, diagnostic.headerColor)
        : undefined,
    [
      diagnostic?.customBrandingEnabled,
      diagnostic?.accentColor,
      diagnostic?.headerColor,
    ],
  );

  useEffect(() => {
    if (!diagnostic) return;
    try {
      if (accentPalette) {
        localStorage.setItem(
          SPINNER_BRANDING_KEY,
          JSON.stringify({
            accent: accentPalette.accent,
            ring: accentPalette.headerBg,
          }),
        );
      } else {
        localStorage.removeItem(SPINNER_BRANDING_KEY);
      }
    } catch {
      // noop
    }
  }, [diagnostic, accentPalette]);

  useEffect(() => {
    (async () => {
      const preservedSession = sessionStorage.getItem(EXTERNAL_SESSION);
      sessionStorage.clear();
      if (preservedSession !== null) {
        sessionStorage.setItem(EXTERNAL_SESSION, preservedSession);
      }

      if (diagnosticId) {
        try {
          const isValid = await areUrlParamsValid(
            diagnosticId,
            distributionIdFromUrl ?? ConstantValues.EMPTY_STRING,
          );

          if (!isValid) {
            setInvalidAndStop();
            return;
          }

          await sleep(1);
        } catch (e) {
          console.error(e);
          setInvalidAndStop();
          return;
        }
      }

      try {
        const sessionInfo = await getSessionInfo();
        if (sessionInfo?.speechEnabled !== undefined) {
          setIsSpeechEnabled(sessionInfo.speechEnabled);
        }
      } catch (e) {
        console.error("Error fetching session info:", e);
        setIsSpeechEnabled(false);
      }

      try {
        const { acknowledged } = await getPrivacyNoticeStatus();
        setShowPrivacyNotice(!acknowledged);
      } catch (e) {
        console.error(e);
        setShowPrivacyNotice(false);
        dispatch(
          showToast("Could not verify privacy notice status.", "warning"),
        );
      } finally {
        setPrivacyLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (hasFetchedInitialData.current) return;

    hasFetchedInitialData.current = true;

    (async () => {
      setIsLoading(true);
      try {
        if (diagnosticId) {
          try {
            const isValid = await areUrlParamsValid(
              diagnosticId,
              distributionIdFromUrl ?? ConstantValues.EMPTY_STRING,
            );

            if (!isValid) {
              setInvalidAndStop();
              return;
            }

            await sleep(1);
          } catch (e) {
            console.error(e);
            setInvalidAndStop();
            return;
          }
        }

        let result = null;
        let nextObjective: INextObjectiveResponse | null = null;
        try {
          const [diagnosticResult, nextObjectiveResult] = await Promise.all([
            getDiagnosticByIdExternal(),
            getNextObjectiveExternal({
              finished: false,
              distributionId: distributionIdFromUrl ?? undefined,
            }).catch((e) => {
              console.error("Error fetching next objective:", e);
              return null;
            }),
          ]);
          result = diagnosticResult;
          nextObjective = nextObjectiveResult;
        } catch (e) {
          console.error(e);
          setInvalidAndStop();
          return;
        }
        if (!result) {
          setInvalidAndStop();
          return;
        }
        setDiagnostic(result);
        setFoundDiagnosticId(diagnosticId);
        setDistributionId(distributionIdFromUrl ?? undefined);
        setInitialNextObjective(nextObjective);

        const url = new URL(window.location.href);
        url.searchParams.delete("distributionId");
        const newUrl: string = url
          .toString()
          .replace(
            /\/external-diagnostics\/[^/]+\/question\/next/,
            "/external-diagnostics/question/next",
          );
        window.history.replaceState({}, document.title, newUrl);
      } catch (error) {
        console.error("Error fetching diagnostic:", error);
        dispatch(showToast("Failed to load diagnostic", "error"));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (diagnostic?.name) {
      document.title = diagnostic.name;
    }
    return () => {
      document.title = PAGE_TITLES.NH_DIAGNOSTICS;
    };
  }, [diagnostic?.name]);

  const setInvalidAndStop = (): void => {
    setIsInvalidUrl(true);
    setIsLoading(false);
  };

  const handleOnAccept = async () => {
    await acceptConsent({
      consentPayload: {
        accepted: true,
        diagnosticId: diagnosticId ?? ConstantValues.EMPTY_STRING,
        distributionId: distributionIdFromUrl ?? ConstantValues.EMPTY_STRING,
      },
    });
    setShowConsent(false);
    setConsent(CONSENT_ACCEPTED);
  };

  const handleOnDecline = () => {
    setShowConsent(true);
    setConsent(CONSENT_DECLINED);
  };

  const handlePrivacyContinue = async () => {
    await recordPrivacyNotice({
      accepted: true,
      diagnosticId: foundDiagnosticId ?? ConstantValues.EMPTY_STRING,
      distributionId: distributionId ?? ConstantValues.EMPTY_STRING,
    });
    await addStartedInterview();
    setShowPrivacyNotice(false);
  };

  const handleInterviewComplete = () => {
    setShowClosingPage(true);
  };

  const handleExportResponses = async () => {
    //future item
  };

  if (isInvalidUrl) {
    return (
      <ExternalLayout>
        <ExternalDiagnosticsInvalidUrl />
      </ExternalLayout>
    );
  }
  if (isLoading && !diagnostic) {
    return (
      <div
        className={styles.spinnerWrapper}
        style={
          accentPalette
            ? ({
                "--colorBrandStroke1": accentPalette.accent,
                "--colorBrandStroke2Contrast": accentPalette.headerBg,
              } as React.CSSProperties)
            : undefined
        }
      >
        <Spinner size="huge" className={styles.loadingSpinner} />
        <Text size={600} weight="bold">
          Loading NH Diagnostic IQ...
        </Text>
      </div>
    );
  }

  return (
    <ExternalLayout
      headerColor={
        diagnostic?.customBrandingEnabled ? diagnostic?.headerColor : undefined
      }
      headerLogoUrl={
        diagnostic?.customBrandingEnabled
          ? diagnostic?.headerLogoUrl
          : undefined
      }
      hideHeader={privacyLoading || showPrivacyNotice}
    >
      <>
        {!privacyLoading && showPrivacyNotice ? (
          <DataPrivacyNoticePage
            termsUrl={TERMS_AND_CONDITIONS_URL}
            privacyUrl={Constants.PRIVACY_POLICY_URL}
            onAccept={handlePrivacyContinue}
            accentPalette={accentPalette}
          />
        ) : !privacyLoading && !showPrivacyNotice ? (
          showConsent ? (
            <ExternalDiagnosticsConsent
              diagnosticName={diagnostic?.name ?? ConstantValues.EMPTY_STRING}
              diagnosticBeginningMessage={
                diagnostic?.beginningMessage ?? ConstantValues.EMPTY_STRING
              }
              handleOnAccept={handleOnAccept}
              handleOnDecline={handleOnDecline}
              speechEnabled={isSpeechEnabled ?? false}
              accentPalette={accentPalette}
            />
          ) : showClosingPage ? (
            <ExternalDiagnosticsClosing
              diagnosticName={diagnostic?.name ?? ConstantValues.EMPTY_STRING}
              closingMessage={
                diagnostic?.endingMessage ?? ConstantValues.EMPTY_STRING
              }
              onExportResponses={handleExportResponses}
            />
          ) : (
            <ExternalDiagnostics
              diagnostic={diagnostic}
              distributionId={distributionId}
              onInterviewComplete={handleInterviewComplete}
              initialNextObjective={initialNextObjective}
            />
          )
        ) : null}
      </>
    </ExternalLayout>
  );
};
